import React, { useEffect } from "react"
import { X, SlidersHorizontal } from "lucide-react"
import { getDurationOptions, getCreatorSizeOptions, AUDIO_TYPE_OPTIONS, PUBLISHED_WITHIN_OPTIONS, CONTENT_TYPE_META, SENTIMENT_META } from "../constants"

const toggleValue = (arr, value) => (arr.includes(value) ? arr.filter(v => v !== value) : [...arr, value])

const ChipToggle = ({ active, onClick, color = "#F6255A", children }) => (
    <button
        type="button"
        onClick={onClick}
        className={`ugc-filter-chip${active ? " is-active" : ""}`}
        style={active ? { borderColor: color, color, background: `${color}18` } : undefined}
    >
        {children}
    </button>
)

const FilterGroup = ({ label, children }) => (
    <div className="ugc-filter-group">
        <span className="ugc-filter-group-label">{label}</span>
        <div className="ugc-filter-chip-row">{children}</div>
    </div>
)

/**
 * The narrowing side of RECENT_VIDEO_FILTERS.md, as a popup modal rather than
 * an inline panel that pushes the page down - everything here maps straight
 * to a query param the endpoint already understands (see FILTER_PARAM_MAP in
 * ugcExplorerApi.js for the camelCase -> snake_case translation). Chip counts
 * come from `filter_options` (fetched once per app/platform with
 * `include_facets=true`, over the app's *whole* set per §5) so options never
 * vanish as the user narrows down. Filters apply live as they're toggled -
 * "Done" just closes the modal, it isn't a submit step.
 */
const FiltersPanel = ({ platform, filters, onChange, facets, onClear, activeCount, onClose }) => {
    const isInstagram = platform === "instagram"
    const isTikTok = platform === "tiktok"
    const set = (key, value) => onChange({ ...filters, [key]: value })

    useEffect(() => {
        const handleKeyDown = (e) => { if (e.key === "Escape") onClose() }
        document.addEventListener("keydown", handleKeyDown)
        return () => document.removeEventListener("keydown", handleKeyDown)
    }, [onClose])

    const durationOptions = getDurationOptions(platform)
    const creatorSizeOptions = getCreatorSizeOptions(platform)
    const contentTypeFacet = facets?.content_type || []
    const sentimentFacet = facets?.sentiment || []
    const creatorSizeFacet = facets?.creator_size || []
    const durationFacet = facets?.duration || []
    const audioTypeFacet = facets?.audio_type || []
    // Instagram and TikTok both facet their creators under `username`,
    // YouTube under `channel` (INSTAGRAM_REEL_API.md §2 / TIKTOK_VIDEO_API.md
    // §2 vs RECENT_VIDEO_FILTERS.md §2).
    const creatorFacet = (isInstagram || isTikTok ? facets?.username : facets?.channel) || []
    const creatorFilterKey = isInstagram || isTikTok ? "username" : "channel"
    const flags = facets?.flags || {}

    return (
        <div className="ugc-filters-overlay" onClick={onClose}>
            <div className="ugc-filters-modal" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Filters">
                <div className="ugc-filters-modal-header">
                    <span className="d-flex align-items-center gap-2">
                        <SlidersHorizontal size={16} color="#F6255A" />
                        <h3>Filters</h3>
                    </span>
                    <button type="button" onClick={onClose} className="ugc-filters-close" aria-label="Close filters">
                        <X size={18} />
                    </button>
                </div>

                <div className="ugc-filters-modal-body">
                    <FilterGroup label="Duration">
                        {durationOptions.map(opt => {
                            const count = durationFacet.find(f => f.value === opt.value)?.count
                            return (
                                <ChipToggle key={opt.value} active={filters.duration.includes(opt.value)} onClick={() => set("duration", toggleValue(filters.duration, opt.value))}>
                                    {opt.label}{count !== undefined ? ` (${count})` : ""}
                                </ChipToggle>
                            )
                        })}
                    </FilterGroup>

                    <FilterGroup label="Content type">
                        {contentTypeFacet.length > 0 ? (
                            contentTypeFacet.map(f => {
                                const meta = CONTENT_TYPE_META[f.value] || CONTENT_TYPE_META.unknown
                                return (
                                    <ChipToggle key={f.value} active={filters.contentType.includes(f.value)} color={meta.color} onClick={() => set("contentType", toggleValue(filters.contentType, f.value))}>
                                        {meta.label} ({f.count})
                                    </ChipToggle>
                                )
                            })
                        ) : (
                            <span className="ugc-filter-empty">Loading options…</span>
                        )}
                    </FilterGroup>

                    <FilterGroup label="Sentiment">
                        {sentimentFacet.length > 0 ? (
                            sentimentFacet.map(f => {
                                const meta = SENTIMENT_META[f.value] || SENTIMENT_META.unknown
                                return (
                                    <ChipToggle key={f.value} active={filters.sentiment.includes(f.value)} color={meta.color} onClick={() => set("sentiment", toggleValue(filters.sentiment, f.value))}>
                                        {meta.label} ({f.count})
                                    </ChipToggle>
                                )
                            })
                        ) : (
                            <span className="ugc-filter-empty">Loading options…</span>
                        )}
                    </FilterGroup>

                    <FilterGroup label="Creator size">
                        {creatorSizeOptions.map(opt => {
                            const count = creatorSizeFacet.find(f => f.value === opt.value)?.count
                            return (
                                <ChipToggle key={opt.value} active={filters.creatorSize.includes(opt.value)} onClick={() => set("creatorSize", toggleValue(filters.creatorSize, opt.value))}>
                                    {opt.label}{count !== undefined ? ` (${count})` : ""}
                                </ChipToggle>
                            )
                        })}
                    </FilterGroup>

                    {isInstagram && audioTypeFacet.length > 0 && (
                        <FilterGroup label="Audio">
                            {AUDIO_TYPE_OPTIONS.map(opt => {
                                const count = audioTypeFacet.find(f => f.value === opt.value)?.count
                                if (count === undefined) return null
                                return (
                                    <ChipToggle key={opt.value} active={filters.audioType.includes(opt.value)} onClick={() => set("audioType", toggleValue(filters.audioType, opt.value))}>
                                        {opt.label} ({count})
                                    </ChipToggle>
                                )
                            })}
                        </FilterGroup>
                    )}

                    {creatorFacet.length > 0 && (
                        <FilterGroup label={isInstagram || isTikTok ? "Creator" : "Channel"}>
                            <select className="ugc-filter-select" style={{ width: "100%" }} value={filters[creatorFilterKey]} onChange={e => set(creatorFilterKey, e.target.value)}>
                                <option value="">{isInstagram || isTikTok ? "All creators" : "All channels"}</option>
                                {creatorFacet.map(f => (
                                    <option key={f.value} value={f.value}>{isInstagram || isTikTok ? `@${f.value}` : f.value} ({f.count})</option>
                                ))}
                            </select>
                        </FilterGroup>
                    )}

                    {(isInstagram || isTikTok) && (
                        <div className="ugc-filter-group">
                            <span className="ugc-filter-group-label">Hashtag</span>
                            <input
                                type="text"
                                placeholder="e.g. #GorillaTag"
                                className="ugc-filter-input"
                                style={{ width: "100%" }}
                                value={filters.hashtag}
                                onChange={e => set("hashtag", e.target.value)}
                            />
                        </div>
                    )}

                    {/* TikTok has no `audio_type` flag (no original-vs-licensed
                        distinction) - it's a free-text search over the sound's
                        title/author instead (TIKTOK_VIDEO_API.md §2: `music`
                        or `sound`, substring, case-insensitive). */}
                    {isTikTok && (
                        <div className="ugc-filter-row-inline">
                            <div className="ugc-filter-group">
                                <span className="ugc-filter-group-label">Music / sound</span>
                                <input
                                    type="text"
                                    placeholder="e.g. original sound"
                                    className="ugc-filter-input"
                                    value={filters.music}
                                    onChange={e => set("music", e.target.value)}
                                />
                            </div>
                            {/* `keyword` narrows to whichever of the run's up-to-3
                                discovery phrases actually surfaced a given video
                                (§2) - different from the free-text `q` below, which
                                searches the caption/account/sound/summary instead. */}
                            <div className="ugc-filter-group">
                                <span className="ugc-filter-group-label">Discovery keyword</span>
                                <input
                                    type="text"
                                    placeholder="e.g. Gorilla Tag meta quest"
                                    className="ugc-filter-input"
                                    value={filters.keyword}
                                    onChange={e => set("keyword", e.target.value)}
                                />
                            </div>
                        </div>
                    )}

                    <div className="ugc-filter-row-inline">
                        <div className="ugc-filter-group">
                            <span className="ugc-filter-group-label">Min. {isInstagram ? "plays" : "views"}</span>
                            <input
                                type="number"
                                min="0"
                                placeholder="e.g. 100000"
                                className="ugc-filter-input"
                                value={filters.minViews}
                                onChange={e => set("minViews", e.target.value)}
                            />
                        </div>
                        <div className="ugc-filter-group">
                            <span className="ugc-filter-group-label">Published within</span>
                            <select className="ugc-filter-select" value={filters.publishedWithinDays} onChange={e => set("publishedWithinDays", e.target.value)}>
                                {PUBLISHED_WITHIN_OPTIONS.map(opt => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {(isInstagram || isTikTok) && (
                        <div className="ugc-filter-row-inline">
                            <div className="ugc-filter-group">
                                <span className="ugc-filter-group-label">Min. likes</span>
                                <input type="number" min="0" placeholder="e.g. 1000" className="ugc-filter-input" value={filters.minLikes} onChange={e => set("minLikes", e.target.value)} />
                            </div>
                            <div className="ugc-filter-group">
                                <span className="ugc-filter-group-label">Min. comments</span>
                                <input type="number" min="0" placeholder="e.g. 50" className="ugc-filter-input" value={filters.minComments} onChange={e => set("minComments", e.target.value)} />
                            </div>
                            <div className="ugc-filter-group">
                                <span className="ugc-filter-group-label">Min. shares</span>
                                <input type="number" min="0" placeholder="e.g. 50" className="ugc-filter-input" value={filters.minShares} onChange={e => set("minShares", e.target.value)} />
                            </div>
                            {/* `saves` is TikTok-only - the strongest intent
                                signal TikTok reports (§1). */}
                            {isTikTok && (
                                <div className="ugc-filter-group">
                                    <span className="ugc-filter-group-label">Min. saves</span>
                                    <input type="number" min="0" placeholder="e.g. 50" className="ugc-filter-input" value={filters.minSaves} onChange={e => set("minSaves", e.target.value)} />
                                </div>
                            )}
                            <div className="ugc-filter-group">
                                <span className="ugc-filter-group-label">Min. engagement %</span>
                                <input type="number" min="0" step="0.1" placeholder="e.g. 2.5" className="ugc-filter-input" value={filters.minEngagementRate} onChange={e => set("minEngagementRate", e.target.value)} />
                            </div>
                        </div>
                    )}

                    {/* Real follower-band filtering - TikTok-only, since it's
                        the only platform with a genuine follower count per
                        video rather than an observed-reach proxy (§1/§2). */}
                    {isTikTok && (
                        <div className="ugc-filter-row-inline">
                            <div className="ugc-filter-group">
                                <span className="ugc-filter-group-label">Min. followers</span>
                                <input type="number" min="0" placeholder="e.g. 10000" className="ugc-filter-input" value={filters.minFollowers} onChange={e => set("minFollowers", e.target.value)} />
                            </div>
                            <div className="ugc-filter-group">
                                <span className="ugc-filter-group-label">Max. followers</span>
                                <input type="number" min="0" placeholder="e.g. 1000000" className="ugc-filter-input" value={filters.maxFollowers} onChange={e => set("maxFollowers", e.target.value)} />
                            </div>
                        </div>
                    )}

                    <div className="ugc-filter-group">
                        <span className="ugc-filter-group-label">Keyword</span>
                        <input
                            type="text"
                            placeholder={isInstagram ? "Search caption, account, summary…" : isTikTok ? "Search caption, account, sound & summary…" : "Search title, channel, summary…"}
                            className="ugc-filter-input"
                            style={{ width: "100%" }}
                            value={filters.q}
                            onChange={e => set("q", e.target.value)}
                        />
                    </div>

                    {!isInstagram && !isTikTok && (flags.is_short > 0 || flags.is_live > 0 || flags.has_transcript > 0) && (
                        <FilterGroup label="Video kind">
                            {flags.is_short > 0 && (
                                <ChipToggle active={filters.isShort} onClick={() => set("isShort", !filters.isShort)}>Shorts only</ChipToggle>
                            )}
                            {flags.is_live > 0 && (
                                <ChipToggle active={filters.isLive} onClick={() => set("isLive", !filters.isLive)}>Live only</ChipToggle>
                            )}
                            {flags.has_transcript > 0 && (
                                <ChipToggle active={filters.hasTranscript} onClick={() => set("hasTranscript", !filters.hasTranscript)}>Transcript-grounded only</ChipToggle>
                            )}
                        </FilterGroup>
                    )}

                    {isInstagram && (flags.is_verified > 0 || flags.has_caption > 0 || flags.has_hashtags > 0 || flags.mentions_app > 0) && (
                        <FilterGroup label="Reel kind">
                            {flags.is_verified > 0 && (
                                <ChipToggle active={filters.isVerified} onClick={() => set("isVerified", !filters.isVerified)}>Verified accounts only</ChipToggle>
                            )}
                            {flags.has_caption > 0 && (
                                <ChipToggle active={filters.hasCaption} onClick={() => set("hasCaption", !filters.hasCaption)}>Has caption</ChipToggle>
                            )}
                            {flags.has_hashtags > 0 && (
                                <ChipToggle active={filters.hasHashtags} onClick={() => set("hasHashtags", !filters.hasHashtags)}>Has hashtags</ChipToggle>
                            )}
                            {flags.mentions_app > 0 && (
                                <ChipToggle active={filters.mentionsApp} onClick={() => set("mentionsApp", !filters.mentionsApp)}>Mentions app directly</ChipToggle>
                            )}
                        </FilterGroup>
                    )}

                    {/* TikTok has no `is_verified` flag (not reported by the
                        source - use `min_followers` instead, above), but adds
                        `has_music` in its place (§2). */}
                    {isTikTok && (flags.has_caption > 0 || flags.has_hashtags > 0 || flags.has_music > 0 || flags.mentions_app > 0) && (
                        <FilterGroup label="Video kind">
                            {flags.has_caption > 0 && (
                                <ChipToggle active={filters.hasCaption} onClick={() => set("hasCaption", !filters.hasCaption)}>Has caption</ChipToggle>
                            )}
                            {flags.has_hashtags > 0 && (
                                <ChipToggle active={filters.hasHashtags} onClick={() => set("hasHashtags", !filters.hasHashtags)}>Has hashtags</ChipToggle>
                            )}
                            {flags.has_music > 0 && (
                                <ChipToggle active={filters.hasMusic} onClick={() => set("hasMusic", !filters.hasMusic)}>Has music</ChipToggle>
                            )}
                            {flags.mentions_app > 0 && (
                                <ChipToggle active={filters.mentionsApp} onClick={() => set("mentionsApp", !filters.mentionsApp)}>Mentions app directly</ChipToggle>
                            )}
                        </FilterGroup>
                    )}
                </div>

                <div className="ugc-filters-modal-footer">
                    {activeCount > 0 ? (
                        <button type="button" onClick={onClear} className="ugc-filter-clear">
                            <X size={13} /> Clear {activeCount} filter{activeCount === 1 ? "" : "s"}
                        </button>
                    ) : <span />}
                    <button type="button" onClick={onClose} className="ugc-btn-primary" style={{ width: "auto" }}>
                        Done
                    </button>
                </div>
            </div>

            <style>{`
                .ugc-filters-overlay {
                    position: fixed;
                    inset: 0;
                    background: rgba(0,0,0,0.6);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: 20px;
                    /* Above the fixed topbar (#page-topbar) and sidebar, which sit in
                       the low thousands - this needs to clear all of the app chrome,
                       not just the page content it's opened from. */
                    z-index: 10050;
                }
                .ugc-filters-modal {
                    width: min(640px, 100%);
                    max-height: 85vh;
                    background: #21222d;
                    border: 1px solid rgba(255,255,255,0.08);
                    border-radius: 16px;
                    box-shadow: 0 20px 60px rgba(0,0,0,0.5);
                    display: flex;
                    flex-direction: column;
                    overflow: hidden;
                }
                .ugc-filters-modal-header {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    padding: 18px 20px;
                    border-bottom: 1px solid rgba(255,255,255,0.06);
                    flex-shrink: 0;
                }
                .ugc-filters-modal-header h3 {
                    color: #fff;
                    font-weight: 600;
                    font-size: 17px;
                    margin: 0;
                    font-family: Rubik, sans-serif;
                }
                .ugc-filters-close {
                    background: rgba(255,255,255,0.05);
                    border: none;
                    color: #e8e8e8;
                    width: 32px;
                    height: 32px;
                    border-radius: 8px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    cursor: pointer;
                }
                .ugc-filters-close:hover { background: rgba(255,255,255,0.1); }
                .ugc-filters-modal-body {
                    display: flex;
                    flex-direction: column;
                    gap: 18px;
                    padding: 20px;
                    overflow-y: auto;
                }
                .ugc-filters-modal-footer {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 12px;
                    padding: 16px 20px;
                    border-top: 1px solid rgba(255,255,255,0.06);
                    flex-shrink: 0;
                }
                .ugc-filter-group { display: flex; flex-direction: column; gap: 8px; }
                .ugc-filter-group-label {
                    font-size: 11px;
                    font-weight: 700;
                    color: #9ca3af;
                    text-transform: uppercase;
                    letter-spacing: 0.04em;
                }
                .ugc-filter-chip-row { display: flex; flex-wrap: wrap; gap: 8px; }
                .ugc-filter-row-inline { display: flex; flex-wrap: wrap; gap: 16px; }
                .ugc-filter-row-inline .ugc-filter-group { flex: 1 1 180px; }
                .ugc-filter-chip {
                    background: rgba(255,255,255,0.05);
                    border: 1px solid rgba(255,255,255,0.1);
                    color: #e8e8e8;
                    padding: 6px 12px;
                    border-radius: 999px;
                    font-size: 12.5px;
                    font-weight: 600;
                    font-family: Rubik, sans-serif;
                    cursor: pointer;
                    white-space: nowrap;
                    transition: background 0.15s ease, border-color 0.15s ease;
                }
                .ugc-filter-chip:hover { background: rgba(255,255,255,0.1); }
                .ugc-filter-chip.is-active { border-width: 1px; }
                .ugc-filter-empty { font-size: 12.5px; color: #6b7280; }
                .ugc-filter-input, .ugc-filter-select {
                    background: #1c1d27;
                    border: 1px solid rgba(255,255,255,0.1);
                    color: #fff;
                    padding: 8px 10px;
                    border-radius: 8px;
                    font-size: 13px;
                    font-family: Rubik, sans-serif;
                    width: 100%;
                    min-width: 0;
                }
                .ugc-filter-input:focus, .ugc-filter-select:focus { outline: none; border-color: #F6255A; }
                .ugc-filter-input::placeholder { color: #6b7280; opacity: 1; }
                .ugc-filter-clear {
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    background: rgba(246,37,90,0.1);
                    border: 1px solid rgba(246,37,90,0.3);
                    color: #F6255A;
                    padding: 8px 12px;
                    border-radius: 8px;
                    font-size: 12.5px;
                    font-weight: 600;
                    font-family: Rubik, sans-serif;
                    cursor: pointer;
                    white-space: nowrap;
                }
                .ugc-filter-clear:hover { background: rgba(246,37,90,0.18); }

                @media (max-width: 575.98px) {
                    .ugc-filters-overlay { padding: 0; align-items: flex-end; }
                    .ugc-filters-modal {
                        width: 100%;
                        max-height: 90vh;
                        border-radius: 16px 16px 0 0;
                    }
                    .ugc-filters-modal-footer { flex-direction: column-reverse; align-items: stretch; }
                    .ugc-filters-modal-footer .ugc-filter-clear,
                    .ugc-filters-modal-footer .ugc-btn-primary { width: 100%; justify-content: center; }
                }
            `}</style>
        </div>
    )
}

export default FiltersPanel
